import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Autoplay } from 'swiper/modules';
import { FaShieldAlt, FaComments, FaStar, FaBolt, FaRocket, FaLock, FaMobileAlt, FaCode, FaPalette, FaGraduationCap, FaCheckCircle, FaChevronDown, FaArrowLeft } from 'react-icons/fa';
import { SiWordpress, SiReact, SiPhp, SiMongodb } from 'react-icons/si';
import ServiceIcon from '../components/ServiceIcon';
import HomeLogoStoryModal from '../components/HomeLogoStoryModal';
import DailyProductShowcase from '../components/DailyProductShowcase';
import { api } from '../services/api';
import { useLocale } from '../context/LocaleContext';
import { getServiceRouteKey, hydrateServices } from '../utils/serviceContent';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import './Home.css';

const Home = () => {
  const { t } = useTranslation();
  const { locale, localePath, formatNumber } = useLocale();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageTitle, setSelectedImageTitle] = useState('');
  const [connectionPaths, setConnectionPaths] = useState([]);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [direction, setDirection] = useState(1); // 1 for right, -1 for left
  const [managedServices, setManagedServices] = useState(() => hydrateServices([]));
  const featuresSectionRef = useRef(null);
  const scrollIndicatorRef = useRef(null);
  const imageSliderRef = useRef(null);
  const autoPlayIntervalRef = useRef(null);
  const featureCardRefs = useRef([
    React.createRef(),
    React.createRef(),
    React.createRef(),
    React.createRef(),
  ]).current;
  
  const slideTitles = useMemo(() => [
    t('home.siteTypes.personal'),
    t('home.siteTypes.store'),
    t('home.siteTypes.educational'),
    t('home.siteTypes.corporate'),
    t('home.siteTypes.service'),
    t('home.siteTypes.news'),
  ], [t]);

  const homeServices = useMemo(() => managedServices
    .filter((service) => service.isActive !== false && service.showOnHome !== false)
    .slice(0, 4), [managedServices]);

  useEffect(() => {
    let cancelled = false;
    api.get(`/api/services?locale=${encodeURIComponent(locale.code)}`)
      .then((response) => {
        if (!cancelled) setManagedServices(hydrateServices(response.data));
      })
      .catch((error) => console.error(t('home.loadingServicesError'), error));
    return () => { cancelled = true; };
  }, [locale.code, t]);

  const calculatePaths = useCallback(() => {
    if (!featuresSectionRef.current || !scrollIndicatorRef.current) return;

    const paths = [];
    const featuresRect = featuresSectionRef.current.getBoundingClientRect();
    const indicatorRect = scrollIndicatorRef.current.getBoundingClientRect();
    
    // نقطه هدف در scroll-indicator (بالای آن)
    const targetX = indicatorRect.left + indicatorRect.width / 2;
    const targetY = indicatorRect.top;
    
    // موقعیت نسبی برای SVG
    const svgStartY = featuresRect.top;
    const svgHeight = targetY - featuresRect.top;
    const svgWidth = Math.max(featuresRect.width, indicatorRect.width);

    featureCardRefs.forEach((ref, index) => {
      if (ref.current) {
        const cardRect = ref.current.getBoundingClientRect();
        const startX = cardRect.left + cardRect.width / 2;
        const startY = cardRect.bottom;
        
        // محاسبه موقعیت نسبی
        const relativeStartX = startX - featuresRect.left;
        const relativeStartY = startY - svgStartY;
        const relativeTargetX = targetX - featuresRect.left;
        const relativeTargetY = targetY - svgStartY;
        
        // نقطه کنترل برای منحنی نرم
        const controlX = (relativeStartX + relativeTargetX) / 2;
        const controlY = relativeStartY + (relativeTargetY - relativeStartY) * 0.5;
        
        paths.push({
          d: `M ${relativeStartX} ${relativeStartY} Q ${controlX} ${controlY} ${relativeTargetX} ${relativeTargetY}`,
          index,
          svgHeight,
          svgWidth
        });
      }
    });

    if (paths.length > 0) {
      setConnectionPaths(paths);
    }
  }, [featureCardRefs]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculatePaths();
    }, 100);

    // Throttle function for scroll events
    let scrollTimeout;
    const throttledCalculatePaths = () => {
      if (scrollTimeout) return;
      scrollTimeout = setTimeout(() => {
        calculatePaths();
        scrollTimeout = null;
      }, 100); // Throttle to max once per 100ms
    };

    // Throttle function for resize events
    let resizeTimeout;
    const throttledResize = () => {
      if (resizeTimeout) return;
      resizeTimeout = setTimeout(() => {
        calculatePaths();
        resizeTimeout = null;
      }, 200); // Throttle to max once per 200ms
    };

    window.addEventListener('resize', throttledResize, { passive: true });
    window.addEventListener('scroll', throttledCalculatePaths, { passive: true });
    
    setTimeout(calculatePaths, 300);
    setTimeout(calculatePaths, 800);

    return () => {
      clearTimeout(timeoutId);
      if (scrollTimeout) clearTimeout(scrollTimeout);
      if (resizeTimeout) clearTimeout(resizeTimeout);
      window.removeEventListener('resize', throttledResize);
      window.removeEventListener('scroll', throttledCalculatePaths);
    };
  }, [calculatePaths]);

  const updateSliderPosition = useCallback((clientX) => {
    if (!imageSliderRef.current) return;
    
    const rect = imageSliderRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  const handleSliderMouseDown = useCallback((e) => {
    setIsDragging(true);
    setIsAutoPlaying(false);
    updateSliderPosition(e.clientX);
    e.preventDefault();
    e.stopPropagation();
  }, [updateSliderPosition]);

  const handleSliderMouseMove = useCallback((e) => {
    if (!isDragging) return;
    updateSliderPosition(e.clientX);
  }, [isDragging, updateSliderPosition]);

  const handleSliderMouseUp = useCallback(() => {
    setIsDragging(false);
    // Resume autoplay after a short delay
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 2000);
  }, []);

  const handleSliderClick = useCallback((e) => {
    if (!isDragging && imageSliderRef.current) {
      updateSliderPosition(e.clientX);
    }
  }, [isDragging, updateSliderPosition]);

  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => handleSliderMouseMove(e);
      const handleMouseUp = () => handleSliderMouseUp();
      
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleSliderMouseMove, handleSliderMouseUp]);

  const handleSliderTouchStart = useCallback((e) => {
    setIsDragging(true);
    setIsAutoPlaying(false);
    if (e.touches[0]) {
      updateSliderPosition(e.touches[0].clientX);
    }
    e.preventDefault();
  }, [updateSliderPosition]);

  const handleSliderTouchMove = useCallback((e) => {
    if (!isDragging || !e.touches[0]) return;
    updateSliderPosition(e.touches[0].clientX);
    e.preventDefault();
  }, [isDragging, updateSliderPosition]);

  const handleSliderTouchEnd = useCallback(() => {
    setIsDragging(false);
    // Resume autoplay after a short delay
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 2000);
  }, []);

  useEffect(() => {
    if (isDragging) {
      const handleTouchMove = (e) => handleSliderTouchMove(e);
      const handleTouchEnd = () => handleSliderTouchEnd();
      
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleSliderTouchMove, handleSliderTouchEnd]);

  // Auto-play animation for slider
  useEffect(() => {
    if (!isAutoPlaying || isDragging) {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
        autoPlayIntervalRef.current = null;
      }
      return;
    }

    autoPlayIntervalRef.current = setInterval(() => {
      setSliderPosition((prevPosition) => {
        const step = 0.3; // Smooth step size
        let newPosition = prevPosition + (direction * step);
        
        // Change direction at boundaries
        if (newPosition >= 100) {
          setDirection(-1);
          return 100;
        } else if (newPosition <= 0) {
          setDirection(1);
          return 0;
        }
        
        return newPosition;
      });
    }, 20); // Update every 20ms for smooth animation

    return () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
        autoPlayIntervalRef.current = null;
      }
    };
  }, [isAutoPlaying, isDragging, direction]);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleModalOpen = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleSlideImageClick = useCallback((imageSrc, title) => {
    setSelectedImage(imageSrc);
    setSelectedImageTitle(title);
    setIsImageModalOpen(true);
  }, []);

  const handleImageModalClose = useCallback(() => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
    setSelectedImageTitle('');
  }, []);

  return (
    <div className="home">
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-eyebrow">
              <span aria-hidden="true">{formatNumber(1)}</span>
              {t('home.eyebrow')}
            </div>
            <h1>
              <span className="hero-title-main">
                <span className="approach-highlight">{t('home.title')}</span>
              </span>
            </h1>
            <p className="hero-subtitle">
              {t('home.subtitle')}
            </p>

            <div className="hero-actions">
              <button type="button" className="hero-cta hero-cta-primary" onClick={() => navigate(localePath('/services'))}>
                {t('home.startProject')}
                <FaArrowLeft aria-hidden="true" />
              </button>
            </div>

            <div className="hero-brand-story-minimal">
              <p className="brand-story-text">
                {t('home.brandStory.intro')}
              </p>
              <div className="brand-words-minimal">
                <div className="brand-word-line">
                  <span className="word-kurdish">{t('home.brandStory.wordRoot')}</span>
                  <span className="word-separator">—</span>
                  <span className="word-meaning">{t('home.brandStory.always')}</span>
                </div>
                <div className="brand-word-line">
                  <span className="word-kurdish">{t('home.brandStory.wordMemory')}</span>
                  <span className="word-separator">—</span>
                  <span className="word-meaning">{t('home.brandStory.memory')}</span>
                </div>
              </div>
              <p className="brand-meaning-text">
                <span className="brand-name">{t('home.title')}</span> {t('home.brandStory.meaning')}
              </p>
              <p className="brand-mission-text">
                {t('home.brandStory.mission')}
              </p>
            </div>
          </div>
          <div className="hero-image" onClick={handleModalOpen}>
            <img src="/images/home/background/haryad-logo.webp" alt={t('home.brandStory.logoAlt')} />
            <div className="hero-image-tooltip">
              <span className="tooltip-text">{t('home.brandStory.more')}</span>
              <div className="tooltip-arrow"></div>
            </div>
          </div>
        </div>
      </section>

      <section className="slider-section">
        <div className="slider-bg-svg">
          <svg className="svg-lines" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid meet" style={{overflow: 'hidden'}}>
            <defs>
              <linearGradient id="sliderLineGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgba(102, 126, 234, 0.7)" stopOpacity="0.7" />
                <stop offset="50%" stopColor="rgba(118, 75, 162, 0.6)" stopOpacity="0.6" />
                <stop offset="100%" stopColor="rgba(102, 126, 234, 0.7)" stopOpacity="0.7" />
              </linearGradient>
              <linearGradient id="sliderLineGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgba(118, 75, 162, 0.65)" stopOpacity="0.65" />
                <stop offset="50%" stopColor="rgba(102, 126, 234, 0.6)" stopOpacity="0.6" />
                <stop offset="100%" stopColor="rgba(118, 75, 162, 0.65)" stopOpacity="0.65" />
              </linearGradient>
            </defs>
            {/* خطوط منحنی زیبا */}
            <path d="M0,200 Q300,100 600,200 T1200,200" fill="none" stroke="url(#sliderLineGradient1)" strokeWidth="3" opacity="0.65" className="animated-line" />
            <path d="M0,400 Q400,300 800,400 T1600,400" fill="none" stroke="url(#sliderLineGradient2)" strokeWidth="3" opacity="0.6" className="animated-line" style={{animationDelay: '1s'}} />
            <path d="M0,600 Q200,500 400,600 T800,600" fill="none" stroke="url(#sliderLineGradient1)" strokeWidth="2.5" opacity="0.55" className="animated-line" style={{animationDelay: '2s'}} />
            
            {/* خطوط مورب */}
            <line x1="0" y1="0" x2="1200" y2="800" stroke="url(#sliderLineGradient1)" strokeWidth="2" opacity="0.5" className="animated-line" style={{animationDelay: '0.5s'}} />
            <line x1="1200" y1="0" x2="0" y2="800" stroke="url(#sliderLineGradient2)" strokeWidth="2" opacity="0.45" className="animated-line" style={{animationDelay: '1.5s'}} />
            
            {/* دایره‌های تزئینی */}
            <circle cx="200" cy="150" r="4" fill="rgba(102, 126, 234, 0.7)" className="animated-dot" />
            <circle cx="800" cy="250" r="3.5" fill="rgba(118, 75, 162, 0.65)" className="animated-dot" style={{animationDelay: '0.8s'}} />
            <circle cx="1000" cy="500" r="4" fill="rgba(102, 126, 234, 0.6)" className="animated-dot" style={{animationDelay: '1.2s'}} />
            <circle cx="150" cy="600" r="3" fill="rgba(118, 75, 162, 0.7)" className="animated-dot" style={{animationDelay: '1.8s'}} />
            <circle cx="950" cy="150" r="3.5" fill="rgba(102, 126, 234, 0.65)" className="animated-dot" style={{animationDelay: '2.2s'}} />
          </svg>
        </div>
        <div className="slider-wrapper">
          <Swiper
            key={locale.direction}
            dir={locale.direction}
            effect={'coverflow'}
            grabCursor={true}
            loop={true}
            centeredSlides={true}
            slidesPerView={3}
            coverflowEffect={{
              rotate: 0,
              stretch: 150,
              depth: 80,
              modifier: 6.5,
              slideShadows: false,
            }}
            breakpoints={
              {
                380: {
                  slidesPerView: 1.15,
                  stretch: 30,
                  depth: 50,
                  modifier: 1.5,
                },
                768: {
                  slidesPerView: 2,
                  stretch: 80,
                },
                1024: {
                  slidesPerView: 3,
                  stretch: 100,
                },
              }
            }
            autoplay={{
              delay: 2500,
              disableOnInteraction: false,
            }}
            modules={[EffectCoverflow, Autoplay]}
            className="coverflow-swiper"
          >
          <SwiperSlide>
            <div className="slide-content">
              <div className="slide-badge">{slideTitles[0]}</div>
              <img 
                src="/images/home/slider/slide1.webp" 
                alt={slideTitles[0]}
                onClick={() => handleSlideImageClick('/images/home/slider/slide1.webp', slideTitles[0])}
                style={{ cursor: 'pointer' }}
              />
            </div>
          </SwiperSlide>
          <SwiperSlide>
            <div className="slide-content">
              <div className="slide-badge">{slideTitles[1]}</div>
              <img 
                src="/images/home/slider/slide2.webp" 
                alt={slideTitles[1]}
                onClick={() => handleSlideImageClick('/images/home/slider/slide2.webp', slideTitles[1])}
                style={{ cursor: 'pointer' }}
              />
            </div>
          </SwiperSlide>
          <SwiperSlide>
            <div className="slide-content">
              <div className="slide-badge">{slideTitles[2]}</div>
              <img 
                src="/images/home/slider/slide3.webp" 
                alt={slideTitles[2]}
                onClick={() => handleSlideImageClick('/images/home/slider/slide3.webp', slideTitles[2])}
                style={{ cursor: 'pointer' }}
              />
            </div>
          </SwiperSlide>
          <SwiperSlide>
            <div className="slide-content">
              <div className="slide-badge">{slideTitles[3]}</div>
              <img 
                src="/images/home/slider/slide4.webp" 
                alt={slideTitles[3]}
                onClick={() => handleSlideImageClick('/images/home/slider/slide4.webp', slideTitles[3])}
                style={{ cursor: 'pointer' }}
              />
            </div>
          </SwiperSlide>
          <SwiperSlide>
            <div className="slide-content">
              <div className="slide-badge">{slideTitles[4]}</div>
              <img 
                src="/images/home/slider/slide5.webp" 
                alt={slideTitles[4]}
                onClick={() => handleSlideImageClick('/images/home/slider/slide5.webp', slideTitles[4])}
                style={{ cursor: 'pointer' }}
              />
            </div>
          </SwiperSlide>
          <SwiperSlide>
            <div className="slide-content">
              <div className="slide-badge">{slideTitles[5]}</div>
              <img 
                src="/images/home/slider/slide6.webp" 
                alt={slideTitles[5]}
                onClick={() => handleSlideImageClick('/images/home/slider/slide6.webp', slideTitles[5])}
                style={{ cursor: 'pointer' }}
              />
            </div>
          </SwiperSlide>
        </Swiper>
        </div>
      </section>
      {/* Image Comparison Slider Section */}
      <section className="image-comparison-section">
        <div className="container">
          <div className="image-comparison-header">
            <h2 className="image-comparison-title">
              {t('home.comparisonTitleBefore')}{' '}
              <span className="approach-highlight">{t('home.comparisonTitleHighlight')}</span>
            </h2>
            <p className="image-comparison-description">
              {t('home.comparisonDescription')}
            </p>
          </div>
          
          <div 
            className={`image-comparison-slider ${isDragging ? 'dragging' : ''}`}
            ref={imageSliderRef}
            onClick={handleSliderClick}
            onMouseLeave={handleSliderMouseUp}
          >
            <div className="image-comparison-wrapper">
              {/* Before Image (Background) */}
              <div className="image-comparison-before">
                <img 
                  src="/images/home/comparison/before.webp" 
                  alt={t('home.comparisonBefore')}
                  className="comparison-image"
                />
                <div className="image-label image-label-before">{t('home.comparisonBefore')}</div>
              </div>
              
              {/* After Image (Clipped) */}
              <div 
                className="image-comparison-after"
                style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
              >
                <img 
                  src="/images/home/comparison/after.webp" 
                  alt={t('home.comparisonAfter')}
                  className="comparison-image"
                />
                <div className="image-label image-label-after">{t('home.comparisonAfter')}</div>
              </div>
              
              {/* Slider Handle */}
              <div 
                className="slider-handle"
                style={{ left: `${sliderPosition}%` }}
                onMouseDown={handleSliderMouseDown}
                onTouchStart={handleSliderTouchStart}
              >
                <div className="slider-handle-line"></div>
                <div className="slider-handle-circle">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 9L12 5L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 15L12 19L16 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Services Section - Creative Design */}
      <section className="services-section">
        <div className="services-background-pattern"></div>
        <div className="container">
          <div className="services-header">
            <h2 className="services-title">
              <span className="services-title-main">{t('home.servicesTitle')}</span>
              <span className="services-title-sub">Services</span>
            </h2>
            <p className="services-description">
              {t('home.servicesDescription')}
            </p>
          </div>
          
          <div className="services-creative-layout">
            {/* Connection Lines SVG */}
            <svg className="services-connection-lines" viewBox="0 0 1200 800" preserveAspectRatio="none">
              <defs>
                <linearGradient id="serviceLineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="rgba(102, 126, 234, 0.4)" />
                  <stop offset="50%" stopColor="rgba(118, 75, 162, 0.5)" />
                  <stop offset="100%" stopColor="rgba(34, 211, 238, 0.4)" />
                </linearGradient>
                <filter id="serviceLineGlow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              <path d="M 200 200 Q 300 150 400 200 T 600 200" fill="none" stroke="url(#serviceLineGradient)" strokeWidth="2" opacity="0.3" filter="url(#serviceLineGlow)" />
              <path d="M 600 200 Q 700 150 800 200 T 1000 200" fill="none" stroke="url(#serviceLineGradient)" strokeWidth="2" opacity="0.3" filter="url(#serviceLineGlow)" />
              <path d="M 200 200 Q 300 400 400 600 T 600 600" fill="none" stroke="url(#serviceLineGradient)" strokeWidth="2" opacity="0.3" filter="url(#serviceLineGlow)" />
              <path d="M 600 200 Q 700 400 800 600 T 1000 600" fill="none" stroke="url(#serviceLineGradient)" strokeWidth="2" opacity="0.3" filter="url(#serviceLineGlow)" />
            </svg>

            {/* Service Items */}
            {homeServices.map((service, index) => (
              <Link
                key={service._id || getServiceRouteKey(service)}
                to={localePath(`/services/${service.slug}`)}
                className={`service-item service-item-${index + 1}`}
                aria-label={service.title}
              >
                <div className="service-floating-icon">
                  <div className={`service-icon-orb service-icon-orb-${index + 1}`}></div>
                  <ServiceIcon name={service.icon} className="service-icon-main" />
                </div>
                <div className="service-content">
                  <div className="service-number">{String(index + 1).padStart(2, '0')}</div>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-subtitle">{service.subtitle}</p>
                  <p className="service-description">{service.cardDescription || service.description}</p>
                </div>
                <div className="service-hover-effect"></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <DailyProductShowcase />

      <section className="features-section" ref={featuresSectionRef}>
        <div className="container">
          <h2 className="features-section-title">{t('home.why.prefix')} <span className="approach-highlight">{t('home.title')}</span> {t('home.why.suffix')}</h2>
          <div className="features-grid">
            <div className="feature-card glass-card" ref={featureCardRefs[0]}>
              <h3 className="feature-title">{t('home.why.refundTitle')}</h3>
              <div className="feature-icon"><FaShieldAlt /></div>
              <p className="feature-description">
                {t('home.why.refundText')}
              </p>
            </div>

            <div className="feature-card glass-card" ref={featureCardRefs[1]}>
              <h3 className="feature-title">{t('home.why.consultTitle')}</h3>
              <div className="feature-icon"><FaComments /></div>
              <p className="feature-description">
                {t('home.why.consultText')}
              </p>
            </div>

            <div className="feature-card glass-card" ref={featureCardRefs[2]}>
              <h3 className="feature-title">{t('home.why.customTitle')}</h3>
              <div className="feature-icon"><FaStar /></div>
              <p className="feature-description">
                {t('home.why.customText')}
              </p>
            </div>

            <div className="feature-card glass-card" ref={featureCardRefs[3]}>
              <h3 className="feature-title">{t('home.why.commitmentTitle')}</h3>
              <div className="feature-icon"><FaBolt /></div>
              <p className="feature-description">
                {t('home.why.commitmentText')}
              </p>
            </div>
          </div>
        </div>
        
        {/* Connection Lines from Feature Cards to Scroll Indicator */}
        {connectionPaths.length > 0 && connectionPaths[0] && (
          <svg 
            className="feature-connection-lines" 
            viewBox={`0 0 ${connectionPaths[0].svgWidth || 1200} ${connectionPaths[0].svgHeight || 600}`} 
            preserveAspectRatio="none"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: `${connectionPaths[0].svgHeight}px`,
              pointerEvents: 'none',
              zIndex: 0
            }}
          >
            <defs>
              <filter id="featureConnectionGlow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            {connectionPaths.map((path, index) => (
              <g key={index}>
                <path
                  d={path.d}
                  fill="none"
                  stroke="rgba(255, 255, 255, 0.8)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  className="feature-connection-path"
                  filter="url(#featureConnectionGlow)"
                />
              </g>
            ))}
          </svg>
        )}
      </section>

      <section className="tech-section">
        <div className="container">
          <div className="tech-content-wrapper">
          <div className="tech-text-content">
              <h2 className="tech-title">
                {t('home.tech.titleBefore')} <span className="tech-highlight">{t('home.tech.titleHighlight')}</span>
              </h2>
              <p className="tech-description">
                {t('home.tech.description')}
              </p>
              <div className="tech-features">
                <div className="tech-feature-item">
                  <div className="tech-feature-icon"><FaRocket /></div>
                  <div className="tech-feature-text">
                    <h3>{t('home.tech.performanceTitle')}</h3>
                    <p>{t('home.tech.performanceText')}</p>
                  </div>
                </div>
                <div className="tech-feature-item">
                  <div className="tech-feature-icon"><FaLock /></div>
                  <div className="tech-feature-text">
                    <h3>{t('home.tech.securityTitle')}</h3>
                    <p>{t('home.tech.securityText')}</p>
                  </div>
                </div>
                <div className="tech-feature-item">
                  <div className="tech-feature-icon"><FaMobileAlt /></div>
                  <div className="tech-feature-text">
                    <h3>{t('home.tech.responsiveTitle')}</h3>
                    <p>{t('home.tech.responsiveText')}</p>
                  </div>
                </div>
                <div className="tech-feature-item">
                  <div className="tech-feature-icon"><FaCheckCircle /></div>
                  <div className="tech-feature-text">
                    <h3>{t('home.tech.architectureTitle')}</h3>
                    <p>{t('home.tech.architectureText')}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="tech-image-container">
              <div className="tech-main-image">
                <img src="/images/home/tech/development.webp" alt={t('home.tech.imageAlt')} />
              </div>
              
              {/* Floating Tech Icons */}
              <div className="floating-icon icon-wordpress">
                <SiWordpress />
              </div>
              <div className="floating-icon icon-react">
                <SiReact />
              </div>
              <div className="floating-icon icon-php">
                <SiPhp />
              </div>
              <div className="floating-icon icon-mongodb">
                <SiMongodb />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scroll Indicator - Visual Element to Encourage Scrolling */}
      <div className="scroll-indicator-wrapper">
        <div className="scroll-indicator" ref={scrollIndicatorRef}>
          {/* Smooth Curved SVG Line */}
          <svg className="scroll-line-svg" viewBox="0 0 100 120" preserveAspectRatio="none">
            <defs>
              <linearGradient id="scrollLineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="rgba(102, 126, 234, 0.9)" />
                <stop offset="30%" stopColor="rgba(118, 75, 162, 0.8)" />
                <stop offset="60%" stopColor="rgba(34, 211, 238, 0.7)" />
                <stop offset="100%" stopColor="rgba(6, 182, 212, 0.5)" />
              </linearGradient>
              <filter id="scrollLineGlow">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            {/* Smooth curved path */}
            <path 
              d="M 50 0 Q 60 30 50 60 Q 40 90 50 120" 
              fill="none" 
              stroke="url(#scrollLineGradient)" 
              strokeWidth="3" 
              strokeLinecap="round"
              className="scroll-path"
              filter="url(#scrollLineGlow)"
            />
            {/* Animated highlight line */}
            <path 
              d="M 50 0 Q 60 30 50 60 Q 40 90 50 120" 
              fill="none" 
              stroke="rgba(255, 255, 255, 0.4)" 
              strokeWidth="1" 
              strokeLinecap="round"
              className="scroll-path-highlight"
            />
          </svg>
          
          <div className="scroll-icon-container">
            <FaChevronDown className="scroll-icon" />
          </div>
          
          {/* Wave Animation Instead of Dots */}
          <div className="scroll-waves">
            <svg className="wave-svg" viewBox="0 0 200 40" preserveAspectRatio="none">
              <defs>
                <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(102, 126, 234, 0.6)" />
                  <stop offset="50%" stopColor="rgba(34, 211, 238, 0.8)" />
                  <stop offset="100%" stopColor="rgba(102, 126, 234, 0.6)" />
                </linearGradient>
              </defs>
              <path 
                d="M 0 20 Q 50 10 100 20 T 200 20" 
                fill="none" 
                stroke="url(#waveGradient)" 
                strokeWidth="2" 
                className="wave-path wave-1"
              />
              <path 
                d="M 0 20 Q 50 30 100 20 T 200 20" 
                fill="none" 
                stroke="url(#waveGradient)" 
                strokeWidth="2" 
                className="wave-path wave-2"
              />
            </svg>
          </div>
        </div>
        <div className="scroll-glow-effect"></div>
      </div>

      <section className="approach-section">
        <div className="container">
          <div className="approach-content">
            <div className="approach-main-card">
              <div className="approach-header">
                <h2 className="approach-title">
                  {t('home.approach.titleBefore')} <span className="approach-highlight">{t('home.title')}</span>
                </h2>
                <p className="approach-intro">
                  {t('home.approach.subtitle')}
                </p>
              </div>

              <div className="approach-text-content">
                <div className="approach-paragraph">
                  <div className="approach-icon-wrapper">
                    <FaCode className="approach-icon" />
                  </div>
                  <p className="approach-text">
                    {t('home.approach.analysis')}
                  </p>
                </div>

                <div className="approach-paragraph">
                  <div className="approach-icon-wrapper">
                    <FaCheckCircle className="approach-icon" />
                  </div>
                  <p className="approach-text">
                    {t('home.approach.quality')}
                  </p>
                </div>

                <div className="approach-paragraph">
                  <div className="approach-icon-wrapper">
                    <FaPalette className="approach-icon" />
                  </div>
                  <p className="approach-text">
                    {t('home.approach.design')}
                  </p>
                </div>

                <div className="approach-paragraph">
                  <div className="approach-icon-wrapper">
                    <FaGraduationCap className="approach-icon" />
                  </div>
                  <p className="approach-text">
                    {t('home.approach.support')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      <HomeLogoStoryModal isOpen={isModalOpen} onClose={handleModalClose} />

      {/* Image Modal for Slides */}
      {isImageModalOpen && selectedImage && (
        <div className="image-modal-overlay" onClick={handleImageModalClose}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="image-modal-close-btn ui-close-button" onClick={handleImageModalClose} aria-label={t('common.closeImage')}>
              ×
            </button>
            {selectedImageTitle && (
              <div className="image-modal-header">
                <h2 className="image-modal-title">{selectedImageTitle}</h2>
              </div>
            )}
            <div className="image-modal-body">
              <img src={selectedImage} alt={selectedImageTitle || t('common.largeImage')} className="image-modal-img" />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Home;
